import { type EntitySource, type Evaluated } from '@/domain/construction';
import { type Vec2, vec } from '@/domain/math';
import { createEntity } from '@/domain/model';

import { type PointerInput, type Tool, type ToolContext, type ToolResponse } from './Tool';

/**
 * Free-form drawing tools.
 *
 * These differ from everything else in the editor in one important way: what they produce
 * has **no parents**. A hand-drawn rectangle is four coordinates, not four points joined
 * by a construction, so it never participates in the DAG as anything but a leaf.
 *
 * That is a deliberate split, not an omission. A rectangle built from constructed corners
 * would move when its corners move — which is what the polygon tool is for. These are for
 * annotation and sketching, where the user wants a shape that stays where they put it, and
 * giving them a construction they did not ask for would make every stray mark a thing with
 * dependents.
 *
 * The consequence to keep in mind: nothing can be built *on* these. They are drawings, and
 * the snapping layer treats them as such.
 */

/* ------------------------------------------------------------------- drag tools */

type DragState =
  | { readonly name: 'idle' }
  | { readonly name: 'dragging'; readonly from: Vec2; readonly trail: readonly Vec2[] };

/**
 * A tool defined by one drag: press, move, release.
 *
 * `build` receives the start, the current point, and the full trail for tools that need
 * every sample rather than just the corners.
 */
class DragTool implements Tool {
  private state: DragState = { name: 'idle' };

  readonly id: string;
  readonly label: string;
  readonly shortcut: string;
  readonly group = 'Vẽ';
  private readonly prefix: string;
  private readonly build: (from: Vec2, to: Vec2, trail: readonly Vec2[]) => EntitySource | null;
  private readonly previewOf: (from: Vec2, to: Vec2, trail: readonly Vec2[]) => Evaluated | null;
  private readonly tracksTrail: boolean;

  constructor(
    id: string,
    label: string,
    shortcut: string,
    prefix: string,
    build: (from: Vec2, to: Vec2, trail: readonly Vec2[]) => EntitySource | null,
    previewOf: (from: Vec2, to: Vec2, trail: readonly Vec2[]) => Evaluated | null,
    tracksTrail = false
  ) {
    this.id = id;
    this.label = label;
    this.shortcut = shortcut;
    this.prefix = prefix;
    this.build = build;
    this.previewOf = previewOf;
    this.tracksTrail = tracksTrail;
  }

  onPointerDown(_context: ToolContext, input: PointerInput): ToolResponse | null {
    if (input.button !== 0) return null;
    this.state = { name: 'dragging', from: input.world, trail: [input.world] };
    return { cursor: 'crosshair' };
  }

  onPointerMove(_context: ToolContext, input: PointerInput): ToolResponse | null {
    if (this.state.name !== 'dragging') return { cursor: 'crosshair' };

    const trail = this.tracksTrail ? sample(this.state.trail, input.world) : this.state.trail;
    this.state = { name: 'dragging', from: this.state.from, trail };

    const preview = this.previewOf(this.state.from, input.world, trail);
    return { preview: preview === null ? [] : [preview], cursor: 'crosshair' };
  }

  onPointerUp(context: ToolContext, input: PointerInput): ToolResponse | null {
    if (this.state.name !== 'dragging') return null;

    const { from, trail } = this.state;
    this.state = { name: 'idle' };

    const source = this.build(
      from,
      input.world,
      this.tracksTrail ? sample(trail, input.world) : trail
    );
    // A click with no drag is not a shape. Returning nothing keeps a stray click from
    // littering the document with degenerate marks.
    if (source === null) return { preview: [] };

    const id = context.nextId(this.prefix);
    return {
      actions: [{ kind: 'add-entity', entity: createEntity(id, source, context.style) }],
      selection: [id],
      preview: [],
    };
  }

  cancel(): ToolResponse | null {
    if (this.state.name === 'idle') return null;
    this.state = { name: 'idle' };
    return { preview: [] };
  }

  reset(): void {
    this.state = { name: 'idle' };
  }
}

/**
 * Appends to the trail, dropping samples the pointer barely moved for.
 *
 * A pointer at 120 Hz produces far more points than a curve needs, and every one of them
 * is stored, serialised and repainted forever. Thinning at capture is the only place the
 * cost can be avoided; thinning later would mean the file already contains the noise.
 */
function sample(trail: readonly Vec2[], next: Vec2): readonly Vec2[] {
  const last = trail[trail.length - 1];
  if (last !== undefined && vec.distance(last, next) < FREEHAND_MIN_STEP) return trail;
  return [...trail, next];
}

const FREEHAND_MIN_STEP = 2;

/** Below this, a drag is a click and produces nothing. */
const MIN_DRAG = 2;

const dragged = (from: Vec2, to: Vec2): boolean => vec.distance(from, to) >= MIN_DRAG;

/* ---------------------------------------------------------------------- shapes */

/** The four corners of the axis-aligned box spanned by a drag. */
function corners(from: Vec2, to: Vec2): readonly Vec2[] {
  return [from, vec.vec2(to.x, from.y), to, vec.vec2(from.x, to.y)];
}

export function createRectangleTool(): Tool {
  return new DragTool(
    'rectangle',
    'Chữ nhật',
    'u',
    'rect',
    (from, to) =>
      dragged(from, to) ? { op: 'free-polyline', points: corners(from, to), closed: true } : null,
    (from, to) => ({ kind: 'polyline', points: corners(from, to), closed: true })
  );
}

/**
 * An ellipse inscribed in the dragged box.
 *
 * Axis-aligned, hence `rotation: 0`. A rotated free ellipse has no handle to rotate it by
 * yet, and shipping a parameter the user cannot reach would be a lie in the file format.
 */
export function createEllipseTool(): Tool {
  const shape = (from: Vec2, to: Vec2) => ({
    center: vec.vec2((from.x + to.x) / 2, (from.y + to.y) / 2),
    rx: Math.abs(to.x - from.x) / 2,
    ry: Math.abs(to.y - from.y) / 2,
    rotation: 0,
  });

  return new DragTool(
    'ellipse',
    'Elip',
    'o',
    'ell',
    (from, to) => (dragged(from, to) ? { op: 'free-ellipse', ...shape(from, to) } : null),
    (from, to) => ({ kind: 'ellipse', ellipse: shape(from, to) })
  );
}

export function createFreehandTool(): Tool {
  return new DragTool(
    'freehand',
    'Vẽ tay',
    'h',
    'ink',
    // Freehand ignores the endpoints: the trail is the shape.
    (_from, _to, trail) =>
      trail.length >= 2 ? { op: 'free-polyline', points: trail, closed: false } : null,
    (_from, _to, trail) => ({ kind: 'polyline', points: trail, closed: false }),
    true
  );
}

/* -------------------------------------------------------------- polyline tool */

type PolylineState =
  { readonly name: 'idle' } | { readonly name: 'drawing'; readonly points: readonly Vec2[] };

/**
 * A click-per-vertex polyline.
 *
 * Distinct from the polygon tool: this one stores raw coordinates and stays open, where
 * polygon creates point entities and closes a ring. Both are in the brief because they
 * answer different questions — "draw a path" and "construct a shape".
 *
 * Ends on Enter, on Escape, or by clicking the last vertex again. Three endings because a
 * path the user cannot put down is a trap, and unlike a polygon there is no first vertex
 * to return to.
 */
export class PolylineTool implements Tool {
  readonly id = 'polyline';
  readonly label = 'Đường gấp khúc';
  readonly shortcut = 'y';
  readonly group = 'Vẽ';

  private state: PolylineState = { name: 'idle' };

  onPointerDown(context: ToolContext, input: PointerInput): ToolResponse | null {
    if (input.button !== 0) return null;

    if (this.state.name === 'idle') {
      this.state = { name: 'drawing', points: [input.world] };
      return { cursor: 'crosshair' };
    }

    const last = this.state.points[this.state.points.length - 1];
    if (last !== undefined && vec.distance(last, input.world) < MIN_DRAG) {
      return this.finish(context);
    }

    this.state = { name: 'drawing', points: [...this.state.points, input.world] };
    return { cursor: 'crosshair' };
  }

  onPointerMove(_context: ToolContext, input: PointerInput): ToolResponse | null {
    if (this.state.name !== 'drawing') return { cursor: 'crosshair' };
    return {
      preview: [{ kind: 'polyline', points: [...this.state.points, input.world], closed: false }],
      cursor: 'crosshair',
    };
  }

  onKeyDown(context: ToolContext, key: string): ToolResponse | null {
    return key === 'Enter' ? this.finish(context) : null;
  }

  cancel(): ToolResponse | null {
    if (this.state.name === 'idle') return null;
    this.state = { name: 'idle' };
    return { preview: [] };
  }

  reset(): void {
    this.state = { name: 'idle' };
  }

  private finish(context: ToolContext): ToolResponse {
    if (this.state.name !== 'drawing' || this.state.points.length < 2) {
      this.state = { name: 'idle' };
      return { preview: [] };
    }

    const points = this.state.points;
    this.state = { name: 'idle' };

    const id = context.nextId('pl');
    return {
      actions: [
        {
          kind: 'add-entity',
          entity: createEntity(id, { op: 'free-polyline', points, closed: false }, context.style),
        },
      ],
      selection: [id],
      preview: [],
    };
  }
}

/** Every free-form drawing tool. */
export function createDrawingTools(): readonly Tool[] {
  return [createRectangleTool(), createEllipseTool(), new PolylineTool(), createFreehandTool()];
}
